import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { css } from '@emotion/core'
import { bool, string } from 'prop-types'
import { useUser, useUserProfile } from '@fs/zion-user'
import { colors } from '@fs/zion-ui'
import { useSpring, animated, config } from '@react-spring/web'
import { getSessionId } from '@fs/zion-session'
import { cdnRoot } from '../../services'

const containerCss = css`
  position: relative;
  width: 212px;
  height: 128px;
  display: inline-block;
`

const circleCss = css`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  background: ${colors.gray10};
  border: 4px solid ${colors.gray00};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 50%;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const activityCircleCss = css`
  ${circleCss}
  z-index: 2;
  transform: translateY(-50%);
  & > img {
    object-position: center -70%;
  }
`

const userCircleBaseCss = css`
  ${circleCss}
  z-index: 1;
  margin-top: -48px;
  transform-origin: 50% 50%;
`

const userMotionCss = css`
  position: absolute;
  left: 0;
  top: 50%;
  z-index: 1;
`

export default function StoryPreviewImages({ activityGroupImageUrl, userImageUrl, isActive = true }) {
  const [animate, setAnimate] = useState(false)
  const user = useUser()
  const sessionId = getSessionId()
  const { userProfile } = useUserProfile()

  const { x, rotate } = useSpring({
    from: { x: 0, rotate: -20 },
    x: animate ? 58 : 0,
    rotate: animate ? 0 : -20,
    config: config.gentle,
    delay: 300,
  })

  const fallbackUserImageUrl = useMemo(() => {
    const gender = user?.gender?.toLowerCase() ?? 'unknown'
    const url = new URL(`${cdnRoot}/connect/avatars/gender-${gender}.png`, window.location.origin)
    url.searchParams.set('cacheBuster', String(Date.now()))
    return url.toString()
  }, [user?.gender])

  const displayUserImageUrl = useMemo(() => {
    if (userImageUrl) return userImageUrl

    if (userProfile?.portrait?.links?.thumb200sUri) {
      const url = new URL(userProfile.portrait.links.thumb200sUri)
      url.searchParams.set('cacheBuster', String(Date.now()))
      if (sessionId) {
        url.searchParams.set('sessionId', sessionId)
      }
      return url.toString()
    }

    return ''
  }, [userImageUrl, userProfile, sessionId])

  const [resolvedUserSrc, setResolvedUserSrc] = useState('')

  useEffect(() => {
    setResolvedUserSrc(displayUserImageUrl || fallbackUserImageUrl)
  }, [displayUserImageUrl, fallbackUserImageUrl])

  const handleUserImgError = useCallback(() => {
    setResolvedUserSrc((current) => (current === fallbackUserImageUrl ? current : fallbackUserImageUrl))
  }, [fallbackUserImageUrl])

  const [resolvedActivitySrc, setResolvedActivitySrc] = useState('')
  useEffect(() => {
    setResolvedActivitySrc(activityGroupImageUrl)
  }, [activityGroupImageUrl])

  useEffect(() => {
    if (!isActive) {
      setAnimate(false)
      return undefined
    }

    // If the finish screen becomes active after a transition, re-trigger the animation.
    setAnimate(false)
    const timer = setTimeout(() => {
      setAnimate(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [isActive])

  return (
    <div css={containerCss} data-testid="story-preview-images">
      <div css={activityCircleCss} aria-hidden="true">
        {resolvedActivitySrc ? <img src={resolvedActivitySrc} alt="" /> : null}
      </div>
      <animated.div
        css={userMotionCss}
        aria-hidden="true"
        style={{
          transform: x.to((val) => `translateY(-50%) translate(${val}px, 0px)`),
        }}
      >
        <animated.div
          css={userCircleBaseCss}
          style={{
            transform: rotate.to((val) => `rotate(${val}deg)`),
          }}
        >
          {resolvedUserSrc ? <img src={resolvedUserSrc} alt="" onError={handleUserImgError} /> : null}
        </animated.div>
      </animated.div>
    </div>
  )
}

StoryPreviewImages.propTypes = {
  activityGroupImageUrl: string,
  userImageUrl: string,
  isActive: bool,
}
